import { ContentType } from "@brookbush/database";
import { StructuredDataService } from "@brookbush/structured-data";
import type { Course } from "@brookbush/types";

import type { AxiosError } from "axios";
import clsx from "clsx";
import { format } from "date-fns";
import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Image from "next/image";
import { NextSeo } from "next-seo";

import CourseAppBar from "./components/CourseAppBar";
import CourseConversionModal from "./components/CourseConversionModal";
import CourseExamsCard from "./components/CourseExamsCard";
import CourseFreeCourseCard from "./components/CourseFreeCourseCard";
import CourseFreeCourseClaimModal from "./components/CourseFreeCourseClaimModal";
import CourseHeader from "./components/CourseHeader";
import CourseInfoModal from "./components/CourseInfoModal";
import CourseInfoPreview from "./components/CourseInfoPreview";
import CoursePassedCard from "./components/CoursePassedCard";
import CourseProgress from "./components/CourseProgress";
import CourseRelatedCourses from "./components/CourseRelatedCourses";
import CourseSections from "./components/CourseSections";
import CourseTutorial from "./components/CourseTutorial";
import AudioPlayer from "~/components/AudioPlayer";
import Comments from "~/components/Comments";
import Container from "~/components/Container";
import ExpressCard from "~/components/ExpressCard";
import LinkedProgramCard from "~/components/LinkedProgramCard";
import NotificationBanner, {
	NotificationBannerType,
} from "~/components/Notifications/NotificationBanner";
import { useAccess, useAuditedCourses, useLinkedProgram } from "~/hooks";
import useChatPageContent from "~/hooks/useChatPageContent";
import useCourseAudio from "~/hooks/useCourseAudio";
import useTrackHistory from "~/hooks/useTrackHistory";
import fetcher from "~/utils/fetcher.client";
import withSanityAudioUrl from "~/utils/withSanityAudioUrl";

const CoursePage = ({
	course,
	structuredData,
}: {
	course: Course;
	structuredData: string;
}) => {
	useTrackHistory({
		contentId: course?._id,
		contentType: ContentType.COURSE,
	});

	useChatPageContent({
		contentType: ContentType.COURSE,
		contentId: course?._id ?? "",
		title: course?.title ?? "",
	});

	const { auditedCourses, isLoading } = useAuditedCourses();

	const { courseAudio } = useCourseAudio(course.slug);

	const auditedCourse = auditedCourses?.find(
		(auditedCourse) => auditedCourse.courseId === course._id,
	);

	const { isLoadingAccess, isMember } = useAccess();

	const { program: linkedProgram } = useLinkedProgram(
		course._id,
		ContentType.COURSE,
	);

	const isLoadingCourseAccess = isLoadingAccess || isLoading;

	const hasCourseAccess = isMember || !!auditedCourse;

	return (
		<>
			{course.isScholarly ? (
				<Head>
					<meta name="citation_title" content={course.title} />
					{course.authors.map((author) => {
						return (
							<meta
								key={author._id}
								name="citation_author"
								content={
									author.firstName && author.lastName
										? `${author.lastName}, ${author.firstName}`
										: author.fullName
								}
							/>
						);
					})}
					<meta
						name="citation_publication_date"
						content={format(new Date(course._createdAt), "yyyy/MM/dd")}
					/>
					<meta
						name="citation_journal_title"
						content="Brookbush Institute Strength & Rehabilitation Review"
					/>
					<meta
						name="citation_abstract_html_url"
						content={`${process.env.NEXT_PUBLIC_SITE_URL}/courses/${course.slug}`}
					/>
					<meta
						name="citation_pdf_url"
						content={`${process.env.NEXT_PUBLIC_API_URL}/courses/${course.slug}/pdf`}
					/>
					{course.keywords ? (
						<meta
							name="citation_keywords"
							content={course.keywords
								?.split(/[,;]/)
								.map((keyword) => keyword.trim())
								.join("; ")}
						/>
					) : null}
				</Head>
			) : null}

			<NextSeo
				title={course.title}
				description={course.description}
				canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/courses/${course.slug}`}
				openGraph={{
					title: course.title,
					url: `${process.env.NEXT_PUBLIC_SITE_URL}/courses/${course.slug}`,
					description: course.description,
					images: [
						{
							url: course.thumbnail?.url || "",
							width: 800,
							height: 600,
							alt: course.title,
						},
					],
				}}
			/>

			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>

			<CourseTutorial course={course} />

			<div className="absolute top-0 left-0 h-48 w-full sm:h-72">
				<Image
					priority
					fetchPriority="high"
					sizes="(min-width: 1024px) 600px, (min-width: 640px) 400px, 300px"
					quality={20}
					className="object-cover object-center"
					src={course.thumbnail?.url || ""}
					alt={course.thumbnail?.alt || course.title}
					fill
				/>

				<div className="absolute top-0 left-0 h-full w-full bg-linear-to-b from-white/90 to-white backdrop-blur dark:from-slate-800/70 dark:to-slate-800" />
			</div>

			<Container key={course._id}>
				{course.isBeta && (
					<div className="relative mb-4 phablet:mb-8 w-full">
						<NotificationBanner type={NotificationBannerType.WARNING}>
							This course has not been published yet. Content may contain
							errors, and is subject to change without notice.
						</NotificationBanner>
					</div>
				)}

				<div className="relative grid grid-cols-10 gap-6 phablet:gap-8">
					<CourseHeader course={course} />

					<div
						className={clsx(
							"col-span-full laptop:col-span-3 laptop:col-start-8 laptop:-col-end-1 laptop:row-span-3 laptop:row-start-1",
						)}
					>
						<div
							className={clsx(
								"laptop:sticky top-34 flex laptop:h-[70vh] flex-col gap-6 phablet:gap-7",
								!isLoadingAccess && !isMember
									? "laptop:h-[75vh]"
									: "laptop:h-[83.5vh]",
							)}
						>
							{!isMember && !isLoadingAccess && (
								<div className="laptop:block hidden">
									<ExpressCard />
								</div>
							)}

							<div className="laptop:block hidden">
								<CourseInfoPreview course={course} />
							</div>

							<CoursePassedCard course={course} />

							<div className="laptop:block hidden">
								<CourseFreeCourseCard course={course} />
							</div>

							<CourseExamsCard
								course={course}
								hasCourseAccess={hasCourseAccess}
								isLoadingCourseAccess={isLoadingCourseAccess}
							/>

							{courseAudio && <AudioPlayer {...courseAudio} isPaid />}

							{linkedProgram && <LinkedProgramCard program={linkedProgram} />}

							<div className="laptop:block hidden">
								<CourseRelatedCourses relatedCourses={course.relatedCourses} />
							</div>
						</div>
					</div>

					<CourseProgress
						course={course}
						isLoadingCourseAccess={isLoadingCourseAccess}
						hasCourseAccess={hasCourseAccess}
					/>

					<div className="col-span-full laptop:hidden">
						<CourseFreeCourseCard course={course} />
					</div>

					<CourseSections course={course} />

					<div className="col-span-full laptop:hidden">
						<ExpressCard />
					</div>

					<div
						className="col-span-full laptop:hidden scroll-m-32"
						id="related-courses"
					>
						<CourseRelatedCourses relatedCourses={course.relatedCourses} />
					</div>

					<Comments
						contentId={course._id}
						contentType={ContentType.COURSE}
						id="course-comments"
					/>
				</div>

				<CourseInfoModal course={course} />

				<CourseConversionModal course={course} />

				<CourseFreeCourseClaimModal course={course} />
			</Container>

			<CourseAppBar course={course} hasAudio={!!courseAudio} />
		</>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	const coursePaths = await fetcher(`/courses/paths`);

	return {
		paths: coursePaths.map((slug: string) => ({
			params: { slug: String(slug) },
		})),
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async (req) => {
	try {
		const slug = req.params?.slug as string;

		const course: Course = await fetcher(`/courses/${slug}`);

		if (!course) {
			return {
				notFound: true,
			};
		}

		const structuredData = await Promise.all([
			StructuredDataService.generateCourse(course, { include: ["*"] }),
			StructuredDataService.getBreadcrumbs(`/courses/${slug}`, (fragment) =>
				fragment === slug ? course.title : undefined,
			),
		]);

		for (const result of structuredData) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		return {
			props: {
				fallback: {
					[`/courses/${course.slug}/audio`]: withSanityAudioUrl(
						await fetcher(`/courses/${course.slug}/audio`).catch(() => null),
					),
				},
				course,
				hasAppBar: true,
				breadcrumbs: [
					{
						name: "Courses",
						href: "/courses",
					},
					{
						name: course.title,
						href: `/courses/${course.slug}`,
					},
				],
				structuredData: StructuredDataService.withContext({
					"@graph": structuredData,
				}),
			},
		};
	} catch (error) {
		if ((error as AxiosError).response?.status === 404) {
			return {
				notFound: true,
			};
		}

		throw error;
	}
};

export default CoursePage;
